import { Controller } from 'stimulus'

// Typeahead client picker for filter forms.
// Expects searchUrl value returning JSON: [{ id, full_name, email }]
export default class extends Controller {
  static targets = ['input', 'hidden', 'results', 'clearButton']
  static values = {
    searchUrl: String,
    emptyMessage: String
  }

  connect() {
    this.searchTimeout = null
    this.boundDocumentClick = this.handleDocumentClick.bind(this)
    document.addEventListener('click', this.boundDocumentClick)
    this.updateClearButton()
  }

  disconnect() {
    document.removeEventListener('click', this.boundDocumentClick)
    if (this.searchTimeout) clearTimeout(this.searchTimeout)
  }

  handleDocumentClick(e) {
    if (!this.element.contains(e.target)) this.hideResults()
  }

  search() {
    const query = this.inputTarget.value.trim()

    if (this.hasHiddenTarget && query !== this.selectedLabel()) {
      this.hiddenTarget.value = ''
    }

    this.updateClearButton()

    if (this.searchTimeout) clearTimeout(this.searchTimeout)
    if (query.length < 2) {
      this.hideResults()
      return
    }

    this.searchTimeout = setTimeout(() => this.fetchClients(query), 300)
  }

  clear() {
    if (this.hasInputTarget) {
      this.inputTarget.value = ''
      this.inputTarget.dataset.selectedLabel = ''
      this.inputTarget.focus()
    }
    if (this.hasHiddenTarget) this.hiddenTarget.value = ''
    this.updateClearButton()
    this.hideResults()
  }

  updateClearButton() {
    if (!this.hasClearButtonTarget || !this.hasInputTarget) return
    this.clearButtonTarget.classList.toggle('hidden', this.inputTarget.value.trim().length === 0)
  }


  async fetchClients(query) {
    const path = this.searchUrlValue
    if (!path) return this.hideResults()

    const url = path.startsWith('http') ? new URL(path) : new URL(path, window.location.origin)
    url.searchParams.set('q', query)

    try {
      const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } })
      if (!response.ok) return this.hideResults()
      this.renderResults(await response.json())
    } catch (_e) {
      this.hideResults()
    }
  }

  renderResults(clients) {
    if (!this.hasResultsTarget) return

    if (!clients.length) {
      this.resultsTarget.innerHTML =
        `<div class="px-3 py-2 text-sm text-gray-500">${this.escapeHtml(this.emptyMessageText())}</div>`
    } else {
      this.resultsTarget.innerHTML = clients
        .map(
          (client) => `
        <button type="button"
                class="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                data-action="click->client-search#select"
                data-client-id="${this.escapeAttr(client.id)}"
                data-client-label="${this.escapeAttr(this.labelFor(client))}">
          ${this.escapeHtml(client.full_name || '')}
          ${client.email ? `<span class="text-gray-500">(${this.escapeHtml(client.email)})</span>` : ''}
        </button>`
        )
        .join('')
    }

    this.resultsTarget.classList.remove('hidden')
  }

  select(element) {
    const { clientId, clientLabel } = element.currentTarget.dataset
    if (this.hasHiddenTarget) this.hiddenTarget.value = clientId || ''
    if (this.hasInputTarget) {
      this.inputTarget.value = clientLabel || ''
      this.inputTarget.dataset.selectedLabel = clientLabel || ''
    }
    this.updateClearButton()
    this.hideResults()
  }

  emptyMessageText() {
    return this.emptyMessageValue || 'No clients found'
  }

  labelFor(client) {
    const name = client.full_name || ''
    return client.email ? `${name} (${client.email})` : name
  }

  selectedLabel() {
    return this.hasInputTarget ? this.inputTarget.dataset.selectedLabel || '' : ''
  }

  hideResults() {
    if (!this.hasResultsTarget) return
    this.resultsTarget.classList.add('hidden')
    this.resultsTarget.innerHTML = ''
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  }

  escapeAttr(value) {
    return this.escapeHtml(value).replace(/'/g, '&#39;')
  }
}
